import axios from '@fs/zion-axios'
import { encodeArk } from '@fs/zion-ark'
import { i18n } from '@fs/zion-locale'
import { RMS_BASE_URL, headers } from '../config'
import { arkToApid } from '../das/dasService'
import getPlaceName from '../standards/places'
import getConceptName from '../standards/concepts'
import { formatWithRules } from '../../utils/dateUtils'

// https://sg32i0.familysearch.org/service/records/rms/resource_ArtifactController.html#resource_ArtifactController_getParentGroupsByGroupType_GET
export async function getArtifact(apid) {
  const url = `${RMS_BASE_URL}/artifact/${apid}`
  return axios.get(url, { headers }).then((response) => {
    const { data } = response
    return data
  })
}

async function getGroupId(ark) {
  const apid = await arkToApid(ark)
  const url = `${RMS_BASE_URL}/artifact/${apid}/parent/grouptype/NATURAL?allowInactive=true`
  const response = await axios.get(url, { headers })
  const { data } = response

  // I don't think that RIP supports deprecated groups so if there is a conflict, we'll return the active one
  if (response.data.length > 1) {
    const url2 = `${RMS_BASE_URL}/artifact/${apid}/parent/grouptype/NATURAL`

    const response2 = await axios.get(url2, { headers })
    const { data: data2 } = response2
    return data2
  }
  return data
}

async function getImages(ark) {
  const groupId = await getGroupId(ark)
  const url = `${RMS_BASE_URL}/artifact/group/${groupId}/children`
  const response = await axios.get(url, { headers })
  const { data } = response
  const images = data?.map((x) => encodeArk(x))
  return images
}

async function getNaturalChildren(groupId) {
  const url = `${RMS_BASE_URL}/group-service/group/${groupId}/natural/children`
  const response = await axios.get(url, { headers })
  const { data } = response
  return data
}

async function getImageApids(groupId) {
  const url = `${RMS_BASE_URL}/artifact/group/${groupId}/children`
  const response = await axios.get(url, { headers })
  const { data } = response
  return data
}

async function generateImageCoverage(coverages) {
  if (!Array.isArray(coverages) || coverages.length === 0) return []

  // Initialize map with unique places and record types
  const coveragesPlaceMap = {}
  coverages.forEach((coverage) => {
    const place = coverage.placeRepId
    if (!coveragesPlaceMap[place]) {
      coveragesPlaceMap[place] = {
        place: { id: place, name: null },
        recordTypes: {},
      }
    }

    const recordTypeId = coverage.recordTypeConceptId
    if (!coveragesPlaceMap[place].recordTypes[recordTypeId]) {
      coveragesPlaceMap[place].recordTypes[recordTypeId] = {
        name: null,
        id: recordTypeId,
        date: [],
      }
    }

    if (coverage.fromDate || coverage.toDate) {
      coveragesPlaceMap[place].recordTypes[recordTypeId].date.push({
        formatted: formatWithRules({
          fromISOString: coverage.fromdateString,
          toISOString: coverage.todateString,
          locale: i18n.language,
        }),
      })
    }
  })

  // Create array of promises for all required API calls
  const placePromises = Object.entries(coveragesPlaceMap).map(async ([placeId, placeData]) => {
    const data = await getPlaceName(placeId, i18n.language)
    const { name: placeName } = data.displayName
    placeData.place.name = placeName

    const recordTypePromises = Object.keys(placeData.recordTypes).map(async (recordTypeId) => {
      const { translation: name } = await getConceptName(recordTypeId, i18n.language)
      placeData.recordTypes[recordTypeId].name = name
    })

    await Promise.all(recordTypePromises)
  })

  // Wait for all API calls to complete
  await Promise.all(placePromises)

  // Format the final output
  return Object.values(coveragesPlaceMap).map((coverage) => ({
    ...coverage,
    recordTypes: Object.values(coverage.recordTypes),
  }))
}

async function getGroupInfo(groupId) {
  const url = `${RMS_BASE_URL}/group-service/group/${groupId}`
  const { data } = await axios.get(url, { headers })

  const { groupName } = data
  let itemNumber
  let dgsNumber
  if (groupName) {
    const parts = groupName.split('_')
    dgsNumber = parts[0]
    itemNumber = parts[1]
    if (itemNumber) itemNumber = parseInt(itemNumber, 10)
  }
  const imageViewerCoverages = await generateImageCoverage(data.coverages)
  const parentId = data?.parentIds?.filter((id) => /^(th)-/i.test(id))?.[0] || null

  return {
    imageViewerCoverages,
    title: data?.title,
    dgsNumber,
    itemNumber,
    creators: data?.creators,
    custodians: data?.custodians,
    coverages: data?.coverages,
    languages: data?.languages || [],
    parentId,
  }
}

/**
 * This call will get permissions, but we have been told to use the themis or platform call to get permissions for now.
 * @param {String} apid - e.g. TH-909-59425-124943-79
 * @returns {Boolean} Has permissions
 */
async function getRmsImagePermissions(apid) {
  const rmsIdEndpoint = `${RMS_BASE_URL}/${apid}/permissions`
  const rmsResponse = await axios.get(rmsIdEndpoint, { headers }).catch((err) => {
    throw new Error(err)
  })
  if (Array.isArray(rmsResponse?.data) && rmsResponse.data.length > 0 && rmsResponse.data[0] === 'ThemisPrmAnyone')
    return true
  return false
}

/**
 * This call will get the external id for an image.
 * @param {String} apid - e.g. TH-909-59425-124943-79
 * @returns {String} externalId
 */
async function getExternalId(apid) {
  const url = `${RMS_BASE_URL}/artifact/${apid}`
  return axios.get(url, { headers }).then((response) => {
    const { data } = response
    return data.externalId
  })
}

export { getGroupId, getImages, getImageApids, getGroupInfo, getRmsImagePermissions, getExternalId, getNaturalChildren }
